import { Box, Button, Grid, Stack, Typography, useTheme } from '@mui/material';
import useClaimableReward from '../../hooks/useClaimableReward';
import { ethers } from 'ethers';
import LoadingButton from '../common/LoadingButton';
import { useState } from 'react';
import ConvertVirtualDrawer from '../../pages/staker/components/ConvertVirtualDrawer';
import UnstakeXVirtualDrawer from '../../pages/staker/components/UnstakeXVirtualDrawer';
import { checkNetwork } from '../../shared/utils/CheckNetwork';
import { useVirtualBalanceContext } from '../../context/VirtualBalanceContext';
import { useAppContext } from '../../context/AppContext';
import { usePrivy } from '@privy-io/react-auth';

const StatItem = ({
  title,
  value,
  desc,
}: {
  title: string;
  value: string;
  desc: string;
}) => {
  return (
    <Grid item xs={12} sm={6} lg={4}>
      <Typography variant={'h5'} fontWeight={'bold'}>
        {title}
      </Typography>
      <Box
        sx={{
          borderLeft: 2,
          borderColor: '#DDDDDD',
          my: 2,
          pl: 2,
        }}
      >
        <Typography pb={1} variant={'h5'} fontWeight={'bold'}>
          {value}
        </Typography>
        <Typography color={'#888888'}>{desc}</Typography>
      </Box>
    </Grid>
  );
};

const OverviewStat = ({
  xVirtualBalance,
  virtualBalance,
}: {
  xVirtualBalance: bigint;
  virtualBalance: bigint;
}) => {
  const theme = useTheme();
  const {
    claimableReward,
    isLoading,
    triggerClaimRewards,
    fetchClaimableReward,
  } = useClaimableReward();

  const { fetchXVirtualBalance, nativeVirtualBalance } =
    useVirtualBalanceContext();

  const { authenticated } = usePrivy();

  const handleTriggerClaimRewards = async () => {
    await triggerClaimRewards();
    await fetchClaimableReward();
    await fetchXVirtualBalance();
  };

  const [openConvertDrawer, setOpenConvertDrawer] = useState<boolean>(false);
  const [openUnstakeDrawer, setOpenUnstakeDrawer] = useState<boolean>(false);
  const { setToggleBridgeModal } = useAppContext();

  const handleOpenDrawer = async (type: string) => {
    await checkNetwork();

    if (type === 'convert') return setOpenConvertDrawer(true);
    if (type === 'withdraw') return setOpenUnstakeDrawer(true);
  };
  return (
    <Grid item xs={12}>
      {nativeVirtualBalance > 0 && (
        <Box
          padding={1}
          pl={2}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          mb={2}
          borderRadius={2}
          boxShadow={'0 1px 20px 0 rgba(0, 0, 0, .1)'}
          sx={{
            background: 'linear-gradient(96deg, #FBF6EA 0%, #E1F5F7 100%)',
          }}
        >
          The VIRTUAL ecosystem operates on the L2 Base chain. To participate,
          you'll need to have $VIRTUAL tokens on the Base chain. For $VIRTUAL
          holders on Ethereum, you will need to bridge your tokens over.
          <Button
            variant="contained"
            style={{ lineHeight: 1 }}
            onClick={() => setToggleBridgeModal(true)}
            sx={{
              px: { xs: '40px', md: 2 },
            }}
          >
            Bridge $VIRTUAL
          </Button>
        </Box>
      )}
      <Box
        p={2}
        sx={{
          backgroundColor: 'white',
        }}
        borderRadius={2}
      >
        <Typography variant={'h5'} fontWeight={'bold'}>
          Overview
        </Typography>
        <Box
          sx={{
            display: 'flex',
            marginTop: 2,
            marginBottom: 2,
            gap: 2,
            [theme.breakpoints.down('sm')]: {
              flexDirection: 'column',
            },
          }}
        >
          <Box
            p={2}
            flex={1}
            borderRadius={2}
            sx={{ border: 1.5, borderColor: '#DDDDDD' }}
            display="flex"
            flexDirection={['column', 'column', 'row', 'row']}
            alignItems={'center'}
            gap={2}
          >
            <Stack flex={2}>
              <Typography pb={1} color={'#888888'}>
                Your wallet $VIRTUAL
              </Typography>
              <Typography variant={'h5'} fontWeight={'bold'}>
                {Number(
                  ethers.formatEther(BigInt(virtualBalance))
                ).toLocaleString('en-US', {
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                })}{' '}
                $VIRTUAL
              </Typography>
              <Typography fontSize={14}>
                Convert to $xVirtual to earn staking rewards and other ecosystem
                benefits{' '}
              </Typography>
            </Stack>
            <Stack flex={1} width={'100%'}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{ whiteSpace: 'nowrap' }}
                onClick={() => {
                  handleOpenDrawer('convert');
                }}
                disabled
              >
                Convert to $xVIRTUAL
              </Button>
            </Stack>
          </Box>
          <Box
            flex={1}
            p={2}
            borderRadius={2}
            sx={{ border: 1.5, borderColor: '#DDDDDD' }}
            display="flex"
            flexDirection={['column', 'column', 'row', 'row']}
            alignItems={'center'}
          >
            <Stack flex={3} width={'100%'}>
              <Typography pb={1} color={'#888888'}>
                Your wallet $xVIRTUAL
              </Typography>

              <Typography variant={'h5'} fontWeight={'bold'}>
                {Number(
                  ethers.formatEther(BigInt(xVirtualBalance))
                ).toLocaleString('en-US', {
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                })}{' '}
                $xVIRTUAL
              </Typography>
              <Box height={'20px'} />
            </Stack>
            <Stack flex={1} width={'100%'}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{ whiteSpace: 'nowrap' }}
                onClick={() => {
                  handleOpenDrawer('withdraw');
                }}
                disabled={!authenticated}
              >
                Convert to $VIRTUAL
              </Button>
              <Typography fontSize={14}>
                Also view your conversion details
              </Typography>
            </Stack>
          </Box>
        </Box>
        <Box
          p={2}
          borderRadius={2}
          sx={{
            display: 'flex',
            marginTop: 2,
            marginBottom: 2,
            border: 1.5,
            borderColor: '#DDDDDD',
            [theme.breakpoints.down('sm')]: {
              flexDirection: 'column',
            },
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Typography pb={1} color={'#888888'}>
              Claimable $VIRTUAL
            </Typography>
            <Typography variant={'h5'} fontWeight={'bold'}>
              {Number(
                ethers.formatEther(BigInt(claimableReward))
              ).toLocaleString('en-US', {
                maximumFractionDigits: 4,
                minimumFractionDigits: 0,
              })}{' '}
              $VIRTUAL
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              [theme.breakpoints.down('sm')]: {
                flexDirection: 'column',
                mt: 1,
              },
            }}
          >
            <LoadingButton
              variant="contained"
              onClick={() => handleTriggerClaimRewards()}
              disabled={claimableReward === BigInt(0)}
              loading={isLoading}
            >
              Claim
            </LoadingButton>
          </Box>
        </Box>
        <Grid
          container
          spacing={3}
          sx={{
            mx: 0,
            mt: 1,
            [theme.breakpoints.up('lg')]: {
              mx: 1,
            },
          }}
        >
          {/*   <StatItem
            title={'Contribution'}
            value={contribution
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            desc={'Contribution Made'}
          ></StatItem> */}
          {/*    <StatItem
            title={'Validation'}
            value={validation.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            desc={'Validation Made'}
          ></StatItem> */}
          {/*  <StatItem
            title={'Utilized'}
            value={'0'}
            desc={'Virtual Utilized'}
          ></StatItem> */}
          {/*   <StatItem
            title={'Staked'}
            value={staked.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            desc={'Virtual Staked'}
          ></StatItem> */}
        </Grid>
      </Box>
      <ConvertVirtualDrawer
        isOpen={openConvertDrawer}
        onClose={() => setOpenConvertDrawer(false)}
      />
      <UnstakeXVirtualDrawer
        isOpen={openUnstakeDrawer}
        onClose={() => setOpenUnstakeDrawer(false)}
      />
    </Grid>
  );
};

export default OverviewStat;
